import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './MyCasesCalendar.css';

const ClosedCasesCalendar = ({ onBack, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date()); // Current month/year
  const [selectedDate, setSelectedDate] = useState(new Date()); // Today
  const [viewMode, setViewMode] = useState('2weeks'); // '2weeks', '1week', '1month'
  const [searchTerm, setSearchTerm] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  // Fetch closed cases from API
  const fetchClosedCases = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/get/case`;
      console.log('Fetching cases from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      console.log('API Response status:', response.status);

      const data = await response.json();
      console.log('API Response:', data);

      if (response.ok && data.data && Array.isArray(data.data)) {
        // Transform API data to match our case structure and filter for closed cases
        const transformedCases = data.data
          .filter(apiCase => apiCase.close_status === 1) // Only include closed cases
          .map(apiCase => {
            // Use institution_date for calendar display
            const institutionDate = new Date(apiCase.institution_date);
            const dateStr = `${institutionDate.getFullYear()}-${String(institutionDate.getMonth() + 1).padStart(2, '0')}-${String(institutionDate.getDate()).padStart(2, '0')}`;
            
            return {
              id: apiCase.id,
              date: dateStr,
              caseNumber: apiCase.case_number,
              title: `CASE #${apiCase.case_number}`,
              partyA: apiCase.party_a,
              partyB: apiCase.party_b,
              refName: `${apiCase.party_a} vs ${apiCase.party_b}`,
              caseType: apiCase.case_type,
              description: apiCase.description,
              clientName: apiCase.client_name,
              clientPhone: apiCase.client_phone,
              courtName: apiCase.court_name,
              judgeName: apiCase.judge_name,
              caseLocation: apiCase.case_location,
              institutionDate: apiCase.institution_date,
              status: 'Closed',
              createdAt: apiCase.created_at,
              proceedings: apiCase.proceedings || [],
            };
          });

        setCases(transformedCases);
        console.log('Transformed closed cases:', transformedCases);
        console.log('Total closed cases:', transformedCases.length);
      } else {
        setError(data.message || 'Failed to fetch cases');
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError(err.message || 'Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch cases on component mount
  useEffect(() => {
    fetchClosedCases();
  }, [token, fetchClosedCases]);

  // Handle case deletion
  const handleDeleteCase = async (caseId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this case?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeleting(caseId);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/delete/case/${caseId}`;
      console.log('Deleting case from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      console.log('Delete response status:', response.status);

      const data = await response.json();
      console.log('Delete response:', data);

      if (response.ok) {
        // Remove the deleted case from the list
        setCases(cases.filter(c => c.id !== caseId));
        console.log('Case deleted successfully');
        Swal.fire({
          title: 'Deleted!',
          text: 'Case has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete case',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error deleting case:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to delete case',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeleting(null);
    }
  };

  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get the first day of the month (0 = Monday, 6 = Sunday for Monday-start calendar)
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert from Sunday=0 to Monday=0 format
    return day === 0 ? 6 : day - 1;
  };

  // Get all closed cases for a specific date
  const getCasesForDate = useCallback((date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    // All cases are already filtered to be closed, so just filter by date
    return cases.filter(c => c.date === dateStr);
  }, [cases]);

  // Get closed cases count for each date (respecting search filter)
  const getCasesCountForDate = useCallback((date) => {
    let filtered = getCasesForDate(date);
    
    // Apply search filter if present
    if (searchTerm.trim()) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.refName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered.length;
  }, [getCasesForDate, searchTerm]);

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Get selected cases for the date
  const selectedCases = useMemo(() => {
    let filtered = getCasesForDate(selectedDate);
    if (searchTerm.trim()) {
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.refName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [selectedDate, searchTerm, getCasesForDate]);

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day empty"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const isSelected = selectedDate.toDateString() === date.toDateString();
      const casesCount = getCasesCountForDate(date);

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''} ${casesCount > 0 ? 'has-cases' : ''}`}
          onClick={() => setSelectedDate(date)}
        >
          <div className="day-number">{day}</div>
          {casesCount > 0 && (
            <span className="cases-badge">{casesCount}</span>
          )}
        </div>
      );
    }

    return days;
  };

  // Get view mode days
  const getViewModeDays = () => {
    if (viewMode === '1week') return 7;
    if (viewMode === '2weeks') return 14;
    if (viewMode === '1month') return 30;
    return 14;
  };

  const viewModeDays = getViewModeDays();

  return (
    <div className="my-cases-container">
      {/* Header */}
      <div className="cases-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>Closed Cases</h2>
        <div style={{ width: '40px' }}></div>
      </div>

      {/* Search Container */}
      <div className="search-container">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          className="search-input"
          placeholder="Search by case number or parties..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <button 
          className={`mode-button ${viewMode === '2weeks' ? 'active' : ''}`}
          onClick={() => setViewMode('2weeks')}
        >
          2 weeks
        </button>
        <button
          className={`mode-button ${viewMode === '1week' ? 'active' : ''}`}
          onClick={() => setViewMode('1week')}
        >
          1 week
        </button>
        <button 
          className={`mode-button ${viewMode === '1month' ? 'active' : ''}`}
          onClick={() => setViewMode('1month')}
        >
          1 month
        </button>
      </div>

      {/* Content */}
      <div className="cases-content">
        {/* Calendar Section */}
        <div className="calendar-section">
          <div className="month-navigation">
            <button className="nav-button" onClick={handlePrevMonth}>
              ←
            </button>
            <span className="month-label">
              {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
            </span>
            <button className="nav-button" onClick={handleNextMonth}>
              →
            </button>
          </div>

          {/* Day Headers */}
          <div className="calendar-header">
            <div className="day-header">MON</div>
            <div className="day-header">TUE</div>
            <div className="day-header">WED</div>
            <div className="day-header">THU</div>
            <div className="day-header">FRI</div>
            <div className="day-header">SAT</div>
            <div className="day-header">SUN</div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
        </div>

        {/* Cases List Section */}
        <div className="cases-list-section">
          <div className="selected-date-info">
            <h3>
              {selectedDate.toLocaleDateString('default', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} ({selectedCases.length} {selectedCases.length === 1 ? 'case' : 'cases'})
            </h3>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Loading closed cases...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : selectedCases.length > 0 ? (
            <div className="cases-list">
              {selectedCases.map(caseItem => (
                <div key={caseItem.id} className="case-item">
                  <div className="case-header">
                    <div className="case-title-section">
                      <span className="case-number">{caseItem.title}</span>
                      <span className="case-type">{caseItem.caseType}</span>
                    </div>
                    <div className="case-header-actions">
                      <span className={`case-status ${caseItem.status.toLowerCase()}`}>
                        {caseItem.status}
                      </span>
                      <button
                        className="delete-button"
                        onClick={() => handleDeleteCase(caseItem.id)}
                        disabled={deleting === caseItem.id}
                        title="Delete case"
                      >
                        {deleting === caseItem.id ? '⏳' : '🗑️'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="case-parties">
                    <p className="party-info">{caseItem.partyA}</p>
                    <p className="party-info">{caseItem.partyB}</p>
                  </div>

                  <div className="case-details">
                    {caseItem.clientName && (
                      <div className="detail-row">
                        <span className="detail-label">Client:</span>
                        <span className="detail-value">{caseItem.clientName}</span>
                      </div>
                    )}
                    {caseItem.clientPhone && (
                      <div className="detail-row">
                        <span className="detail-label">Phone:</span>
                        <span className="detail-value">{caseItem.clientPhone}</span>
                      </div>
                    )}
                    {caseItem.courtName && (
                      <div className="detail-row">
                        <span className="detail-label">Court:</span>
                        <span className="detail-value">{caseItem.courtName}</span>
                      </div>
                    )}
                    {caseItem.judgeName && (
                      <div className="detail-row">
                        <span className="detail-label">Judge:</span>
                        <span className="detail-value">{caseItem.judgeName}</span>
                      </div>
                    )}
                    {caseItem.caseLocation && (
                      <div className="detail-row">
                        <span className="detail-label">Location:</span>
                        <span className="detail-value">{caseItem.caseLocation}</span>
                      </div>
                    )}
                    {caseItem.description && (
                      <div className="detail-row full-width">
                        <span className="detail-label">Description:</span>
                        <p className="detail-value description">{caseItem.description}</p>
                      </div>
                    )}
                  </div>

                  {caseItem.proceedings && caseItem.proceedings.length > 0 && (
                    <div className="case-proceedings">
                      <span className="proceedings-label">Proceedings ({caseItem.proceedings.length})</span>
                      <div className="proceedings-list">
                        {caseItem.proceedings.slice(0, 2).map((proceeding, idx) => (
                          <div key={idx} className="proceeding-item">
                            <span className="proceeding-date">
                              {new Date(proceeding.datetime).toLocaleDateString()}
                            </span>
                            <span className="proceeding-note">{proceeding.note}</span>
                          </div>
                        ))}
                        {caseItem.proceedings.length > 2 && (
                          <span className="more-proceedings">+{caseItem.proceedings.length - 2} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="no-cases">
              <p>No closed cases found for this date</p>
              {searchTerm && (
                <button 
                  className="clear-search-button"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClosedCasesCalendar;
